'use client';

import { ColumnDef } from '@tanstack/react-table';
import { TransactionDetails } from '../../types';
import { t } from 'i18next';
import { Icon, Typography } from '@/components';
import { IconName } from '@/components/icons/types';
import { useCountries } from '@/hooks';
import { cn } from '@/utils/styles';
import { orderTechTransactionIcons } from '@/helpers';
import { useCompanyRole } from '@/features/company/hooks';

interface CreateColumnsOptions {
  expandedRowId: string | null;
  toggleRow: (id: string) => void;
}

export const columns = ({ expandedRowId, toggleRow }: CreateColumnsOptions): ColumnDef<TransactionDetails>[] => [
  {
    accessorKey: 'date',
    header:  t('Date'),
    cell: ({ row }) => {
      const date: Date = row.getValue('date') ?? '-';
      const formatted = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        year: 'numeric',
      });
      return (
        <div className="w-1/6">
          <Typography as="p" size="body-2" weight="normal" className="text-left">
            {formatted.format(date)}
          </Typography>
        </div>
      );
    },
  },
  {
    accessorKey: 'technologies',
    header: t('Technology'),
    cell: ({ row }) => {
      const technologies = row.getValue('technologies') as string[];
      return (
        <div className="flex">
          {orderTechTransactionIcons(technologies || []).map((technology) => (
            <Icon
              name={technology as IconName}
              className="text-primary-700 mr-2 h-6 w-6"
              key={`${technology}-${row.id}`}
            />
          ))}
        </div>
      );
    },
    filterFn: (row, columnId, filterValue) => {
      const techsRow = row.getValue('technologies') as string[];
      return filterValue.some((value: string) => techsRow.includes(value));
    },
  },
  {
    accessorKey: 'buyer',
    header: t('Buyer'),
    cell: ({ row }) => {
      const formattedBuyer: string = row.getValue('buyer') ?? '-';
      return (
        <div className="max-w-lg min-w-0 leading-relaxed break-words whitespace-normal">
          <Typography as="p" size="body-2" weight="semibold" className="text-left">
            {formattedBuyer}
          </Typography>
        </div>
      );
    },
  },
  {
    accessorKey: 'seller',
    header: t('Seller'),
    cell: ({ row }) => {
      const formattedSeller: string = row.getValue('seller');
      return (
        <div className="max-w-lg min-w-0 leading-relaxed break-words whitespace-normal">
          <Typography as="p" size="body-2" weight="semibold" className="text-left">
            {formattedSeller}
          </Typography>
        </div>
      );
    },
  },
  {
    accessorKey: 'capacity',
    header: t('Capacity'),
    cell: ({ row }) => {
      const capacity = row.getValue('capacity') ?? '-';
      const formattedCapacity = capacity === '-' ? '-' : `${capacity} MW`;
      return (
        <div className="max-w-lg min-w-0 leading-relaxed break-words whitespace-normal">
          <Typography as="p" size="body-2" weight="normal" className="text-left">
            {formattedCapacity}
          </Typography>
        </div>
      );
    },
  },
  {
    accessorKey: 'city',
    header: t('Location'),
    cell: ({ row }) => {
      const formattedCity: string = row.getValue('city') ?? '-';
      return (
        <div className="max-w-lg min-w-0 leading-relaxed break-words whitespace-normal">
          <Typography as="p" size="body-2" weight="normal" className="text-left">
            {formattedCity}
          </Typography>
        </div>
      );
    },
  },
  {
    accessorKey: 'country',
    header: t('Country'),
    cell: ({ row }) => {
      const { options: countries } = useCountries();
      const transactionCountry: string = row.getValue('country') ?? '-';
      const country = countries.find((c) => c.value === transactionCountry)?.label || transactionCountry;
      return (
        <div className="max-w-lg min-w-0">
          <Typography as="p" size="body-2" weight="normal" className="text-left">
            {country}
          </Typography>
        </div>
      );
    },
    filterFn: (row, columnId, filterValue) => {
      const countryRow = row.getValue('country') as string[];
      return filterValue.some((value: string) => countryRow.includes(value));
    },
  },
  {
    accessorKey: 'dropdown',
    header: '',
    cell: ({ row }) => {
      const isExpanded = expandedRowId === row.original.id;
      const { isPremium } = useCompanyRole();
      return (
        <div className="w-1/6 pr-3">
          <button
            type="button"
            className="bg-base-white hover:bg-neutral-25 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-neutral-50 transition hover:shadow-xs"
            onClick={(e) => {
              e.stopPropagation();
              toggleRow(row.original.id);
            }}
          >
            <Icon
              name="chevron-left"
              className={cn(
                'text-base-primary h-6 w-6 transition',
                isPremium && isExpanded ? 'rotate-90' : '-rotate-90'
              )}
            />
          </button>
        </div>
      );
    },
  },
];
